// Copyright 2017 Sorama B.V.

import { computedFrom } from "aurelia-framework";
import { Range } from "datatypes/SoramaLocalApi";
import { StringExtensions } from "extensions/StringExtensions";
import { OctaveBands } from "libraries/Math/OctaveBands";
import { SPLMonitorModule } from "modules/workflows/Streamer/SPL/SPLMonitorModule";
import { SelectableRange } from "services/FrequencySelection/FrequencySelectionService";
import { HardwareSampleRate } from "services/Hardware/HardwareSamplerRate";
import { UnitValue } from "units/Unit";
import { WeightedSpectrum } from "units/WeightedSpectrum";
import { Weighting } from "units/Weighting";

export class SPLEntry {
    //#region Constants
    // Results from the lower frequency of the second octave band (44-88 Hz) which is the first band that does not suffer from DC leakage when streaming
    public mininumFrequency = OctaveBands.OctaveBandFrequencies[1].lower;

    // Get the nyquist frequency given the default sample rate
    public maximumFrequency = HardwareSampleRate.defaultCAMSampleRate / 2;
    //#endregion

    private module: SPLMonitorModule;
    private value: UnitValue;
    private weightedSpectrum: WeightedSpectrum;

    public weighting: Weighting;
    public editMode: boolean;
    public isManualInputBoxVisible: boolean;
    public frequencyRange: SelectableRange;

    public manualInputFrom = 0;
    public manualInputTo = 0;

    @computedFrom("module.frequencyRanges.length")
    public get avialableFrequencyRanges(): SelectableRange[] {
        return this.module.frequencyRanges;
    }

    @computedFrom("value")
    public get integerPart(): string {
        const num = Math.floor(this.value.value);
        return Number.isSafeInteger(num) ? num.toString() : "-";
    }

    @computedFrom("value")
    public get fractionalPart(): string {
        const num = Math.round((this.value.value - Math.floor(this.value.value)) * 10);
        return Number.isSafeInteger(num) ? num.toString() : "-";
    }

    @computedFrom("value")
    public get splValue(): number {
        return this.value.value;
    }

    @computedFrom("value")
    public get name(): string {
        return this.value.unit.unit;
    }

    @computedFrom("module.frequencyRanges.length")
    public get canUseAsSelection(): boolean {
        return (
            this.frequencyRange != null &&
            this.frequencyRange != undefined &&
            (this.module.frequencyRanges == null ||
                this.module.frequencyRanges == undefined ||
                !this.module.frequencyRanges.some(
                    (x) => x.from == this.frequencyRange.from && x.to == this.frequencyRange.to
                ))
        );
    }

    @computedFrom("weightedSpectrum.DataPoints")
    public get step(): number {
        if (!this.weightedSpectrum || this.weightedSpectrum.data.length < 2) return 1;

        return Math.round(this.weightedSpectrum.data[1].x - this.weightedSpectrum.data[0].x);
    }

    constructor(module: SPLMonitorModule, initialValue: UnitValue, weighting: Weighting) {
        this.editMode = false;
        this.module = module;
        this.value = initialValue;
        this.weighting = weighting;
    }

    public removeEntry() {
        this.module.removeEntry(this);
    }

    public update(weightedSpectrum?: WeightedSpectrum) {
        if (weightedSpectrum && weightedSpectrum != this.weightedSpectrum) {
            this.weightedSpectrum = weightedSpectrum;
            this.correctFrequencyRangeForSpectrumBinsize();
        }

        if (this.weightedSpectrum) {
            this.value = this.weightedSpectrum.toPowerValue(
                this.value.unit,
                this.frequencyRange
                    ? this.frequencyRange
                    : <Range>{ from: this.mininumFrequency, to: this.maximumFrequency, isActive: true }
            );
        }
    }

    public setFrequencyRange(newValue: SelectableRange): void {
        this.frequencyRange = newValue;

        this.correctFrequencyRangeForSpectrumBinsize();

        this.update();
    }

    private correctFrequencyRangeForSpectrumBinsize(): void {
        if (this.weightedSpectrum && this.frequencyRange) {
            // Correct for binsize of spectrum
            const spectrum = this.weightedSpectrum;

            // Just make sure that the values dont exceed the bounds of the frequency range
            const from = Math.min(
                Math.max(this.frequencyRange.from, spectrum.originalDataPoints[0].x),
                spectrum.originalDataPoints[spectrum.originalDataPoints.length - 1].x
            );
            const to = Math.min(
                Math.max(this.frequencyRange.to, spectrum.originalDataPoints[0].x),
                spectrum.originalDataPoints[spectrum.originalDataPoints.length - 1].x
            );

            // Round so we only show whole hertz units in the ui
            this.frequencyRange = <SelectableRange>{
                from: Math.round(from),
                to: Math.round(to),
                isSelected: this.frequencyRange.isSelected,
            };
        }
    }

    public toggleEditMode(): void {
        this.editMode = !this.editMode;
        this.isManualInputBoxVisible = false;
    }

    public showManualInputBox(): void {
        if (this.frequencyRange) {
            this.manualInputFrom = this.frequencyRange.from;
            this.manualInputTo = this.frequencyRange.to;
        }

        this.isManualInputBoxVisible = true;
    }

    public onManualInputComplete() {
        this.isManualInputBoxVisible = false;

        this.setFrequencyRange(<SelectableRange>{
            from: Math.min(this.manualInputFrom, this.manualInputTo),
            to: Math.max(this.manualInputFrom, this.manualInputTo),
            isSelected: true,
        });
    }

    public copyValueToClipboard(): void {
        StringExtensions.copyToClipboard(this.integerPart + "." + this.fractionalPart);
    }

    public addRangeToSelection(): void {
        // Add range
        if (!this.module.frequencyRanges) {
            this.module.frequencyRanges = [this.frequencyRange];
            return;
        }

        const ranges = [...this.module.frequencyRanges];
        ranges.push(this.frequencyRange);

        // Find overlapping frequency ranges
        for (let i = 0; i < ranges.length; i++) {
            const r1 = ranges[i];
            for (let j = i + 1; j < ranges.length; j++) {
                const r2 = ranges[j];

                if (
                    (r2.from >= r1.from && r2.from <= r1.to) ||
                    (r2.to >= r1.from && r2.to <= r1.to) ||
                    (r1.from >= r2.from && r1.from <= r2.to) ||
                    (r1.to >= r2.from && r1.to <= r2.to)
                ) {
                    // Overlap, remove both and replace with single range
                    ranges.splice(i, 1, {
                        from: Math.min(r1.from, r2.from),
                        to: Math.max(r1.to, r2.to),
                        isSelected: r1.isSelected || r2.isSelected,
                    });
                    ranges.splice(j, 1);
                    i--;
                    break;
                }
            }
        }

        this.module.frequencyRanges = ranges;
    }
}
